import type { TaskNotificationPreferences } from "@/types/task.types"

/**
 * The per-user task email switches (Tasks → bell icon).
 *
 * GET/PUT /tasks/notification-preferences answer `{ data: {...} }`; api.ts
 * unwraps that once, through readTaskNotificationPreferences below. The dialog
 * used to unwrap a second time (`prefs.data`), always found nothing, fell back
 * to "everything on", and so every toggle sent `false` and snapped back on
 * refetch (FOR-243). Keeping the unwrap and the merge here, typed, is what
 * stops the two layers disagreeing about the shape again.
 */

export const DEFAULT_TASK_NOTIFICATION_PREFERENCES: TaskNotificationPreferences = {
  task_assigned: true,
  task_commented: true,
  task_due_reminder: true,
  task_due_date_changed: true,
  task_completed: true,
}

export const TASK_NOTIFICATION_ITEMS: { key: keyof TaskNotificationPreferences; label: string }[] = [
  { key: "task_assigned", label: "Task assigned to me" },
  { key: "task_commented", label: "Comments on my tasks" },
  { key: "task_due_reminder", label: "Due date reminders" },
  { key: "task_due_date_changed", label: "Due date changed" },
  { key: "task_completed", label: "A task I'm on is marked done" },
]

/** The API's envelope: `{ data: { task_assigned: bool, ... } }`. */
export interface TaskNotificationPreferencesBody {
  data: Partial<TaskNotificationPreferences> | null
}

/**
 * The one place the envelope is opened. A missing key reads as on, matching
 * User::wantsTaskNotification on the API (no preference row means notify).
 */
export function readTaskNotificationPreferences(body: TaskNotificationPreferencesBody): TaskNotificationPreferences {
  return { ...DEFAULT_TASK_NOTIFICATION_PREFERENCES, ...(body.data ?? {}) }
}

/** What the switches show after a toggle, before the server confirms it. */
export function applyPreferenceChange(
  current: TaskNotificationPreferences | undefined,
  change: Partial<TaskNotificationPreferences>,
): TaskNotificationPreferences {
  return { ...(current ?? DEFAULT_TASK_NOTIFICATION_PREFERENCES), ...change }
}
